<script>
/* global axios */
import { useAlert } from 'dashboard/composables';
import router from '../../../../index';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: { NextButton },
  data() {
    return {
      step: 1,
      inboxName: '',
      enableGroups: false,
      isCreating: false,
      isConnecting: false,
      instanceToken: null,
      instanceName: null,
      qrCode: null,
      pairCode: null,
      profileName: '',
      instanceStatus: null,
      errorMsg: '',
      pollTimer: null,
    };
  },
  computed: {
    accountId() {
      return window.location.pathname.split('/')[3];
    },
    apiBase() {
      return `/api/v1/accounts/${this.accountId}/uazapi`;
    },
    statusLabel() {
      const map = {
        connected: 'Conectado',
        connecting: 'Aguardando leitura do QR Code...',
        disconnected: 'Desconectado',
      };
      return map[this.instanceStatus] || this.instanceStatus || '';
    },
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    async startSetup() {
      if (!this.inboxName.trim()) return;
      this.isConnecting = true;
      this.errorMsg = '';
      try {
        const { data } = await axios.post(`${this.apiBase}/create_instance`, {
          inbox_name: this.inboxName.trim(),
        });
        this.instanceToken = data.token;
        this.instanceName = data.instance_name;
        this.step = 2;
        await this.connectInstance();
      } catch (e) {
        this.errorMsg =
          e?.response?.data?.error || 'Erro ao criar instância UaZapi.';
      } finally {
        this.isConnecting = false;
      }
    },
    async connectInstance() {
      this.isConnecting = true;
      this.errorMsg = '';
      this.qrCode = null;
      try {
        const { data } = await axios.post(`${this.apiBase}/connect`, {
          instance_token: this.instanceToken,
        });
        if (data.qrcode) {
          this.qrCode = data.qrcode;
        }
        if (data.paircode) {
          this.pairCode = data.paircode;
        }
        this.instanceStatus = 'connecting';
        this.startPolling();
      } catch (e) {
        this.errorMsg =
          e?.response?.data?.error || 'Falha ao gerar QR Code.';
      } finally {
        this.isConnecting = false;
      }
    },
    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(async () => {
        try {
          const { data } = await axios.post(`${this.apiBase}/status`, {
            instance_token: this.instanceToken,
          });
          const st = data.status;
          if (st) this.instanceStatus = st;
          if (data.profile_name) this.profileName = data.profile_name;
          if (data.qrcode) this.qrCode = data.qrcode;

          if (st === 'connected') {
            this.stopPolling();
            this.step = 3;
            await this.finalize();
          }
        } catch {
          // ignore
        }
      }, 3000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async finalize() {
      this.isCreating = true;
      this.errorMsg = '';
      try {
        const { data } = await axios.post(`${this.apiBase}/setup`, {
          instance_token: this.instanceToken,
          instance_name: this.instanceName,
          inbox_name: this.inboxName.trim(),
          enable_groups: this.enableGroups,
        });
        useAlert('Caixa de entrada UaZapi criada com sucesso!');
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: data.inbox_id },
        });
      } catch (e) {
        this.errorMsg =
          e?.response?.data?.error || 'Erro ao finalizar configuração.';
      } finally {
        this.isCreating = false;
      }
    },
    async retryQr() {
      this.stopPolling();
      await this.connectInstance();
    },
  },
};
</script>

<template>
  <div class="max-w-lg">
    <!-- STEP 1: NOME -->
    <div v-if="step === 1">
      <div class="flex items-center gap-3 mb-5">
        <div
          class="w-9 h-9 rounded-xl flex items-center justify-center"
          style="background: #25D366"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </div>
        <div>
          <h2 class="text-base font-semibold text-n-slate-12">
            UaZapi WhatsApp
          </h2>
          <p class="text-xs text-n-slate-11">
            Conecte um número via QR Code
          </p>
        </div>
      </div>

      <form class="flex flex-col gap-4" @submit.prevent="startSetup">
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            Nome da Caixa de Entrada
          </label>
          <input
            v-model="inboxName"
            type="text"
            placeholder="Ex: WhatsApp Vendas"
            class="w-full px-3 py-2 rounded-lg border border-n-slate-4 bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-[#25D366]/50 focus:border-[#25D366]"
          />
        </div>

        <label
          class="flex items-center gap-3 cursor-pointer select-none"
        >
          <input
            v-model="enableGroups"
            type="checkbox"
            class="w-4 h-4 rounded border-n-slate-4 text-[#25D366] focus:ring-[#25D366]"
          />
          <span class="text-sm text-n-slate-12">
            Receber mensagens de grupos
          </span>
        </label>

        <p
          v-if="errorMsg"
          class="text-sm text-n-ruby-11 bg-n-ruby-3 rounded-lg px-3 py-2"
        >
          {{ errorMsg }}
        </p>

        <NextButton
          :is-loading="isConnecting"
          :disabled="!inboxName.trim()"
          type="submit"
          solid
          blue
          label="Conectar WhatsApp"
        />
      </form>
    </div>

    <!-- STEP 2: QR CODE -->
    <div v-if="step === 2">
      <div class="flex items-center gap-3 mb-4">
        <div
          class="w-9 h-9 rounded-xl flex items-center justify-center"
          style="background: #25D366"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </div>
        <div>
          <h2 class="text-base font-semibold text-n-slate-12">
            Escaneie o QR Code
          </h2>
          <p class="text-xs text-n-slate-11">
            {{ statusLabel }}
          </p>
        </div>
      </div>

      <!-- QR -->
      <div
        v-if="qrCode"
        class="flex flex-col items-center gap-4 p-6 bg-white rounded-xl border border-n-slate-3"
      >
        <img
          :src="qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`"
          alt="QR Code WhatsApp"
          class="w-60 h-60 rounded-lg"
        />
        <p class="text-sm text-n-slate-11 text-center leading-relaxed">
          Abra o <strong>WhatsApp</strong> no celular<br />
          Vá em <strong>Aparelhos Conectados</strong><br />
          Toque em <strong>Conectar um Aparelho</strong>
        </p>
        <div class="flex items-center gap-2">
          <span class="relative flex h-2.5 w-2.5">
            <span
              class="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
              style="background: #25D366"
            />
            <span
              class="relative inline-flex rounded-full h-2.5 w-2.5"
              style="background: #25D366"
            />
          </span>
          <span class="text-xs text-n-slate-10">Aguardando leitura...</span>
        </div>
      </div>

      <!-- Loading -->
      <div
        v-else-if="isConnecting"
        class="flex flex-col items-center gap-3 p-8"
      >
        <div
          class="w-8 h-8 border-3 border-n-slate-4 border-t-[#25D366] rounded-full animate-spin"
        />
        <p class="text-sm text-n-slate-11">Gerando QR Code...</p>
      </div>

      <p
        v-if="errorMsg"
        class="mt-4 text-sm text-n-ruby-11 bg-n-ruby-3 rounded-lg px-3 py-2"
      >
        {{ errorMsg }}
      </p>

      <div class="flex gap-2 mt-4">
        <NextButton
          variant="outline"
          label="QR Code expirou? Gerar novo"
          @click="retryQr"
        />
      </div>
    </div>

    <!-- STEP 3: FINALIZANDO -->
    <div v-if="step === 3">
      <div class="flex flex-col items-center gap-4 py-8">
        <div
          class="w-14 h-14 rounded-full flex items-center justify-center"
          style="background: #22c55e"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
          </svg>
        </div>
        <h2 class="text-lg font-semibold text-n-slate-12">
          WhatsApp conectado!
        </h2>
        <p
          v-if="profileName"
          class="text-sm text-n-slate-11"
        >
          Perfil: <strong>{{ profileName }}</strong>
        </p>
        <p class="text-sm text-n-slate-11">
          Configurando caixa de entrada...
        </p>
        <div
          v-if="isCreating"
          class="w-8 h-8 border-3 border-n-slate-4 border-t-[#25D366] rounded-full animate-spin"
        />
        <p
          v-if="errorMsg"
          class="text-sm text-n-ruby-11 bg-n-ruby-3 rounded-lg px-3 py-2"
        >
          {{ errorMsg }}
        </p>
      </div>
    </div>
  </div>
</template>
